import React, { useEffect, useState } from "react";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  DialogDescription,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import {
  ErrorMessage,
  Field,
  Fieldset,
  FieldGroup,
  Label,
  Description,
} from "../../../design/fieldset";
import { Select } from "../../../design/select";
import { useVisit } from "../../../contexts/VisitContext";
import { useNavigate } from "react-router-dom";
import { useSupervisor } from "../../../contexts/SupervisorContext";
import { useLocation } from "../../../contexts/LocationContext";
import { Timestamp } from "firebase/firestore";
import { Visit, VisitStatus } from "@visitproof/types";
import { Textarea } from "../../../design/textarea";
import {
  toLocalDateString,
  toLocalTimeString,
} from "../../../utils/timeString";
import moment from "moment";
import { Text } from "../../../design/text";
import {
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  UserIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  PauseCircleIcon,
} from "@heroicons/react/24/solid";

interface Props {
  visit: Visit;
}

interface Errors {
  startDate?: string;
  endDate?: string;
  caseId?: string;
  supervisorId?: string;
}

const EditVisitButton: React.FC<Props> = ({ visit }) => {
  const { locations } = useLocation();
  const { supervisors } = useSupervisor();
  const { updateVisit } = useVisit();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [status, setStatus] = useState<VisitStatus>(visit.status);
  const [startDate, setStartDate] = useState<string>(
    toLocalDateString(visit.startDate.toDate()).slice(0, 10),
  );
  const [endDate, setEndDate] = useState<string>(
    toLocalDateString(visit.endDate.toDate()).slice(0, 10),
  );
  const [startTime, setStartTime] = useState<string>(
    toLocalTimeString(visit.startDate.toDate()).slice(11, 16),
  );
  const [endTime, setEndTime] = useState<string>(
    toLocalTimeString(visit.endDate.toDate()).slice(11, 16),
  );
  const [timeZoneId, setTimeZoneId] = useState<string>(
    visit.timeZoneId ?? moment.tz.guess(),
  );
  const [locationId, setLocationId] = useState<string | undefined>(visit.locationId);
  const [supervisorId, setSupervisorId] = useState<string | undefined>(visit.supervisorId);
  const [notes, setNotes] = useState<string>(visit.notes ?? "");
  const [errors, setErrors] = useState<Errors>({});

  useEffect(() => {
    setStatus(visit.status);
    setStartDate(toLocalDateString(visit.startDate.toDate()).slice(0, 10));
    setEndDate(toLocalDateString(visit.endDate.toDate()).slice(0, 10));
    setStartTime(toLocalTimeString(visit.startDate.toDate()).slice(11, 16));
    setEndTime(toLocalTimeString(visit.endDate.toDate()).slice(11, 16));
    setTimeZoneId(visit.timeZoneId ?? moment.tz.guess());
    setLocationId(visit.locationId);
    setSupervisorId(visit.supervisorId);
    setNotes(visit.notes ?? "");
  }, [visit]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!startDate.trim()) {
      newErrors.startDate = "Start date is required.";
    }
    if (!endDate.trim()) {
      newErrors.endDate = "End date is required.";
    }
    const start = new Date(`${startDate}T${startTime}`);
    const end = new Date(`${endDate}T${endTime}`);
    if (start && end && new Date(start) > new Date(end)) {
      newErrors.startDate = "Start date must be before end date.";
    }
    if (end && start && new Date(end) < new Date(start)) {
      newErrors.endDate = "End date must be after start date.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setIsSaving(true);
    setError(null);

    try {
      const data = {
        status,
        caseId: visit.caseId,
        locationId,
        location: locations.find((location) => location.id === locationId)!,
        startDate: Timestamp.fromDate(new Date(`${startDate}T${startTime}`)),
        endDate: Timestamp.fromDate(new Date(`${endDate}T${endTime}`)),
        notes,
        timeZoneId,
      } as Visit;

      if (supervisorId) {
        data.supervisorId = supervisorId;
        data.supervisor = supervisors.find(
          (supervisor) => supervisor.id === supervisorId,
        )!;
      }

      const visitId = await updateVisit(visit.id, data);
      navigate(`/visit/${visitId}`);
      clearForm();
    } catch (e) {
      console.error(e);
      setError("Failed to update visit. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = () => {
    setStatus(visit.status);
    setStartDate(toLocalDateString(visit.startDate.toDate()).slice(0, 10));
    setEndDate(toLocalDateString(visit.endDate.toDate()).slice(0, 10));
    setStartTime(toLocalTimeString(visit.startDate.toDate()).slice(11, 16));
    setEndTime(toLocalTimeString(visit.endDate.toDate()).slice(11, 16));
    setTimeZoneId(visit.timeZoneId ?? moment.tz.guess());
    setLocationId(visit.locationId);
    setSupervisorId(visit.supervisorId);
    setNotes(visit.notes ?? "");
    setIsOpen(false);
    setError(null);
  };

  const statusOptions: {
    value: VisitStatus;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
  }[] = [
    {
      value: "pending",
      label: "Pending",
      icon: PauseCircleIcon,
      color: "text-amber-500",
    },
    {
      value: "scheduled",
      label: "Scheduled",
      icon: CalendarDaysIcon,
      color: "text-blue-500",
    },
    {
      value: "completed",
      label: "Completed",
      icon: CheckCircleIcon,
      color: "text-green-500",
    },
    {
      value: "canceled",
      label: "Canceled",
      icon: XCircleIcon,
      color: "text-red-500",
    },
  ];

  const filteredLocations = locations.filter(
    (location) => location.caseId === visit.caseId,
  );
  const activeSupervisors = supervisors.filter((s) => s.status !== "inactive");

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        Edit
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen} size="xl">
        <DialogTitle>Edit Visit</DialogTitle>
        <DialogDescription>
          Update the visit details, schedule, and assignment.
        </DialogDescription>
        <DialogBody className="space-y-6">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
              <div className="flex items-center gap-2">
                <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                <Text className="text-sm text-red-700 dark:text-red-300">
                  {error}
                </Text>
              </div>
            </div>
          )}

          {/* Status Selection */}
          <div>
            <Text className="mb-3 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
              Visit Status
            </Text>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {statusOptions.map((option) => {
                const isSelected = status === option.value;
                const StatusIcon = option.icon;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setStatus(option.value)}
                    disabled={isSaving}
                    className={`flex flex-col items-center gap-1.5 rounded-lg border p-3 transition-colors ${
                      isSelected
                        ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500 dark:border-indigo-400 dark:bg-indigo-900/20"
                        : "border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800 dark:hover:border-zinc-600"
                    } ${isSaving ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                  >
                    <StatusIcon
                      className={`h-6 w-6 ${isSelected ? option.color : "text-zinc-400"}`}
                    />
                    <Text
                      className={`text-xs font-medium ${
                        isSelected
                          ? "text-indigo-700 dark:text-indigo-300"
                          : "text-zinc-600 dark:text-zinc-400"
                      }`}
                    >
                      {option.label}
                    </Text>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date & Time Section */}
          <Fieldset>
            <FieldGroup>
              <div className="rounded-lg border border-zinc-200 p-4 dark:border-zinc-700">
                <div className="mb-4 flex items-center gap-2">
                  <CalendarDaysIcon className="h-5 w-5 text-zinc-500" />
                  <Text className="font-medium text-zinc-900 dark:text-zinc-100">
                    Schedule
                  </Text>
                </div>
                <div className="space-y-4">
                  <Field>
                    <Label>Start</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        disabled={isSaving}
                      />
                      <Input
                        type="time"
                        step="900"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        disabled={isSaving}
                      />
                    </div>
                    {errors.startDate && (
                      <ErrorMessage>{errors.startDate}</ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>End</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={isSaving}
                      />
                      <Input
                        type="time"
                        step="900"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        disabled={isSaving}
                      />
                    </div>
                    {errors.endDate && (
                      <ErrorMessage>{errors.endDate}</ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Time Zone</Label>
                    <Select
                      value={timeZoneId}
                      onChange={(e) => setTimeZoneId(e.target.value)}
                      disabled={isSaving}
                    >
                      {moment.tz.names().map((tz) => (
                        <option key={tz} value={tz}>
                          {tz.replace(/_/g, " ")}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>
              </div>
            </FieldGroup>
          </Fieldset>

          {/* Assignment Section */}
          <Fieldset>
            <FieldGroup>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg border border-zinc-200 p-4 dark:border-zinc-700">
                  <div className="mb-3 flex items-center gap-2">
                    <UserIcon className="h-5 w-5 text-zinc-500" />
                    <Text className="font-medium text-zinc-900 dark:text-zinc-100">
                      Monitor
                    </Text>
                  </div>
                  <Field>
                    <Select
                      value={supervisorId || ""}
                      onChange={(e) => setSupervisorId(e.target.value)}
                      disabled={isSaving}
                    >
                      <option value="">Select a supervisor</option>
                      {activeSupervisors.map((supervisor) => (
                        <option key={supervisor.id} value={supervisor.id}>
                          {supervisor.firstName} {supervisor.lastName}
                        </option>
                      ))}
                    </Select>
                    {activeSupervisors.length === 0 && (
                      <Description className="mt-1">
                        No active supervisors available
                      </Description>
                    )}
                  </Field>
                </div>

                <div className="rounded-lg border border-zinc-200 p-4 dark:border-zinc-700">
                  <div className="mb-3 flex items-center gap-2">
                    <MapPinIcon className="h-5 w-5 text-zinc-500" />
                    <Text className="font-medium text-zinc-900 dark:text-zinc-100">
                      Location
                    </Text>
                  </div>
                  <Field>
                    <Select
                      value={locationId || ""}
                      onChange={(e) => setLocationId(e.target.value)}
                      disabled={isSaving}
                    >
                      <option value="">Select a location</option>
                      {filteredLocations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </Select>
                    {filteredLocations.length === 0 && (
                      <Description className="mt-1">
                        No locations for this case
                      </Description>
                    )}
                  </Field>
                </div>
              </div>
            </FieldGroup>
          </Fieldset>

          {/* Notes Section */}
          <Fieldset>
            <FieldGroup>
              <div className="rounded-lg border border-zinc-200 p-4 dark:border-zinc-700">
                <div className="mb-3 flex items-center gap-2">
                  <DocumentTextIcon className="h-5 w-5 text-zinc-500" />
                  <Text className="font-medium text-zinc-900 dark:text-zinc-100">
                    Notes
                  </Text>
                </div>
                <Field>
                  <Textarea
                    value={notes}
                    placeholder="Add any notes or special instructions for this visit..."
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={isSaving}
                    rows={3}
                  />
                </Field>
              </div>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm} disabled={isSaving}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditVisitButton;
